import type { Client } from '@snapshots/transport';

/** One allowance of an organization, as the service keeps it: never rewritten, ended by a later record */
export interface Allowance {
	id: string;
	organization: string;
	plan: string;
	limits: { bytes?: number; sources?: number };
	reason: string;
	granted_by: string;
	granted: string;
	ends?: string;
	ended?: string;
	ended_by?: string;
	ended_reason?: string;
}

/** Where an organization stands at Beyond Accounts, or why that is not known */
export type OrganizationState = 'active' | 'closing' | 'closed' | 'unknown' | 'unavailable';

/** `GET /v1/operator/sources/allowances/{organization}` */
export interface AllowanceReading {
	organization: { id: string; state: OrganizationState };
	/** The open allowance, if any; `active` is whether it is in force now (an open one may be past its `ends`) */
	current: (Allowance & { active: boolean }) | null;
	allowances: Allowance[];
	/** What a grant may take: the installation's own bounds, and how far ahead it may end */
	bounds: { bytes: number; sources: number; days: number };
	/** How recent a sign-in a change needs, in seconds */
	recent: number;
}

/** One line of measured usage: what was measured, never what is charged */
export interface UsageLine {
	kind: string;
	unit: string;
	quantity: number;
	events?: number;
}

export interface UsageReading {
	usage: { organization: string; period: string; lines: UsageLine[]; allowances: Allowance[] };
}

/** What a grant or an end answered, and whether it answered an earlier send of the same request */
export interface AllowanceChange {
	allowance: Allowance;
	replaced: string | null;
	replayed: boolean;
}

/** The terms a grant sends */
export interface GrantTerms {
	plan: string;
	reason: string;
	limits: { bytes: number; sources: number };
	ends?: string;
	expected: string | null;
}

/** The terms an end sends */
export interface EndTerms {
	reason: string;
	expected: string;
}

/**
 * A Beyond operator's view of an organization's cloud sources allowance: reading it, its history, where the
 * organization stands and the month's measured usage, and granting or ending one. A change is sent with the key
 * its request holds (`Proposal`), so sending the same request again after a lost answer answers what the first
 * send decided and never grants twice.
 */
export class Allowances {
	#client: Client;

	constructor(client: Client) {
		this.#client = client;
	}

	static #path(organization: string): string {
		return `/v1/operator/sources/allowances/${encodeURIComponent(organization)}`;
	}

	read(organization: string): Promise<AllowanceReading> {
		return this.#client.get<AllowanceReading>(Allowances.#path(organization));
	}

	usage(organization: string, period?: string): Promise<UsageReading> {
		const query = period ? `?${new URLSearchParams({ period })}` : '';
		return this.#client.get<UsageReading>(`/v1/operator/sources/usage/${encodeURIComponent(organization)}${query}`);
	}

	grant(organization: string, terms: GrantTerms, key: string): Promise<AllowanceChange> {
		return this.#client.call<AllowanceChange>('PUT', Allowances.#path(organization), terms, { 'idempotency-key': key });
	}

	end(organization: string, terms: EndTerms, key: string): Promise<AllowanceChange> {
		return this.#client.call<AllowanceChange>('DELETE', Allowances.#path(organization), terms, { 'idempotency-key': key });
	}
}
